import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { createRoom, joinRoom, getIdentity } from "./justOneRoom"
import { Screen, JoButton, C, F, useJustOneFonts } from "./JustOneUI"
import { PLAYER_COLORS } from "../../../utils/scattergoriesLogic"

const inputStyle = {
  height: 52,
  borderRadius: 12,
  border: `1.5px solid ${C.borderDark}`,
  background: C.card,
  padding: "0 16px",
  fontSize: 17,
  fontWeight: 700,
  fontFamily: F.body,
  color: C.ink,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.2s ease",
}

const JustOneLanding = () => {
  useJustOneFonts()
  const navigate = useNavigate()
  const [name, setName] = useState(getIdentity().name)
  const [code, setCode] = useState("")
  const [mode, setMode] = useState(null) // null | "create" | "join"
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const handleCreate = async () => {
    if (!name.trim()) return
    setBusy(true)
    setError("")
    try {
      const newCode = await createRoom(name.trim())
      navigate(`/justone/${newCode}`)
    } catch (e) {
      setError("Couldn't create a game. Try again.")
      setBusy(false)
    }
  }

  const handleJoin = async () => {
    if (!name.trim() || !code.trim()) return
    setBusy(true)
    setError("")
    try {
      await joinRoom(code.trim().toUpperCase(), name.trim())
      navigate(`/justone/${code.trim().toUpperCase()}`)
    } catch (e) {
      setError(
        e.message === "not-found"
          ? "No game with that code. Check it and try again."
          : "Couldn't join. Try again."
      )
      setBusy(false)
    }
  }

  return (
    <Screen style={{ justifyContent: "center", gap: 16 }}>
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        {/* Playful Multi-colored Logo */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            fontFamily: F.display,
            fontWeight: 900,
            fontSize: 48,
            letterSpacing: -1.5,
            marginBottom: 8,
            filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.08))",
          }}
        >
          {["J", "u", "s", "t", " ", "O", "n", "e"].map((char, i) => (
            <span
              key={i}
              style={{
                color: char === " " ? "transparent" : PLAYER_COLORS[i % PLAYER_COLORS.length],
                display: "inline-block",
                transform: i % 2 === 0 ? "rotate(-4deg)" : "rotate(4deg) translateY(2px)",
                width: char === " " ? "12px" : "auto",
              }}
            >
              {char}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.muted, fontFamily: F.display }}>
          The cooperative word guessing game!
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 800, color: C.muted, letterSpacing: 0.5, pl: 4 }}>
          YOUR NAME
        </label>
        <input
          style={inputStyle}
          placeholder="Enter your name"
          value={name}
          maxLength={16}
          onChange={(e) => setName(e.target.value)}
          onFocus={(e) => (e.target.style.borderColor = C.cyan)}
          onBlur={(e) => (e.target.style.borderColor = C.borderDark)}
        />
      </div>

      {mode === "join" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 800, color: C.muted, letterSpacing: 0.5, pl: 4 }}>
            ROOM CODE
          </label>
          <input
            style={{ ...inputStyle, letterSpacing: 2, textTransform: "uppercase" }}
            placeholder="ROOM CODE (e.g. PEACH-82)"
            value={code}
            maxLength={9}
            onChange={(e) => setCode(e.target.value)}
            onFocus={(e) => (e.target.style.borderColor = C.cyan)}
            onBlur={(e) => (e.target.style.borderColor = C.borderDark)}
          />
        </div>
      )}

      {error && (
        <div style={{ color: C.coral, fontWeight: 700, fontSize: 14, textAlign: "center" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
        {mode !== "join" ? (
          <>
            <JoButton onClick={handleCreate} disabled={busy || !name.trim()}>
              {busy ? "Creating…" : "Create a Room"}
            </JoButton>
            <JoButton variant="panel" onClick={() => setMode("join")} disabled={busy}>
              Join with Code
            </JoButton>
          </>
        ) : (
          <>
            <JoButton onClick={handleJoin} disabled={busy || !name.trim() || !code.trim()}>
              {busy ? "Joining…" : "Join Game"}
            </JoButton>
            <JoButton variant="ghost" onClick={() => setMode(null)} disabled={busy}>
              Back
            </JoButton>
          </>
        )}
      </div>
    </Screen>
  )
}

export default JustOneLanding
